import React from "react";

export interface PageMastheadProps {
  /** Nav-label line above the title. Sentence case — the canon forbids
   * all-caps, so the eyebrow reads through size, weight and navy ink. */
  eyebrow?: string;
  /** The page's h1. Canon: text-3xl sm:text-4xl font-bold tracking-tight. */
  title: React.ReactNode;
  /** Meta lines under the title. Wrapper carries mt-2 text-sm
   * text-gray-500 (canon — not configurable). */
  meta?: React.ReactNode;
  /** Compact period/view switchers (Home's PeriodTabs). Render at the
   * end of the title row, bottom-aligned beside the headline — adding no
   * masthead height on desktop; flex-wrap drops them below the title
   * block naturally on narrow screens. Date-range pickers and filter
   * pills do NOT belong in the masthead — they sit in page flow directly
   * after the lede (see apps/web/src/CLAUDE.md, "Design canon"). */
  controls?: React.ReactNode;
  /** Right side of the top row, opposite the eyebrow (e.g. a back-link). */
  aside?: React.ReactNode;
  /** Root element. Default <header> (the page masthead). Pass "div" when
   * the masthead nests inside an existing <header> — nested <header>
   * elements are invalid HTML. */
  as?: "header" | "div";
}

const EYEBROW_CLASS = "text-xs font-semibold text-brand-navy";
const TITLE_CLASS = "text-3xl sm:text-4xl font-bold tracking-tight text-gray-900";

/**
 * PageMasthead — the canon page-title block (design canon 2026-07-23;
 * uniformity directive same day: ONE geometry, no per-page variants).
 * Every V2 page opens with this exact composition, modeled on the Home
 * masthead — the canon reference, measured live:
 *
 * - Row 1 (when eyebrow or aside present): eyebrow left, optional aside
 *   right (`flex items-start justify-between gap-4`).
 * - Row 2, mt-2 (8px) below row 1: `flex flex-wrap items-end
 *   justify-between gap-4` — left column is h1 + meta (mt-2 text-sm
 *   text-gray-500); `controls` sit at the row's end, bottom-aligned.
 *
 * The geometry is deliberately NOT configurable — uniformity across pages
 * outranks preserving any page's incumbent spacing (Ari, 2026-07-23).
 * `controls` is for compact period/view switchers only; date-range
 * pickers and filter pills live in page flow after the lede, per the
 * canon rule (Ari's live adjudication, same day).
 * Type, ink, and spacing come from named tokens / canon classes only — no
 * arbitrary hex here, ever (see apps/web/src/CLAUDE.md, "Design canon").
 */
const PageMasthead: React.FC<PageMastheadProps> = ({
  eyebrow,
  title,
  meta,
  controls,
  aside,
  as = "header",
}) => {
  const Root = as;
  const hasTopRow = eyebrow != null || aside != null;
  return (
    <Root>
      {hasTopRow && (
        <div className="flex items-start justify-between gap-4">
          {eyebrow != null ? (
            <span className={EYEBROW_CLASS}>{eyebrow}</span>
          ) : (
            <span aria-hidden="true" />
          )}
          {aside}
        </div>
      )}
      <div
        className={`${hasTopRow ? "mt-2 " : ""}flex flex-wrap items-end justify-between gap-4`}
      >
        <div>
          <h1 className={TITLE_CLASS}>{title}</h1>
          {meta != null && (
            <div className="mt-2 text-sm text-gray-500">{meta}</div>
          )}
        </div>
        {controls}
      </div>
    </Root>
  );
};

export default PageMasthead;
